import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  AppShell,
  Burger,
  Text,
  Group,
  Button,
  Stack,
  UnstyledButton,
  Container,
  Alert,
  Loader,
} from "@mantine/core";
import { useMobileNavbar } from "./hooks/useMobileNavbar";
import {
  IconShield,
  IconUsers,
  IconUserCog,
  IconCalendar,
  IconLogout,
  IconDashboard,
  IconGavel,
  IconClock,
  IconFileText,
  IconBuilding,
  IconClipboardList,
} from "@tabler/icons-react";
import { useAuth } from "./auth/AuthContext";
import { notifications } from "@mantine/notifications";

// Public Pages
import { Landing } from "./pages/Landing";
import { Login } from "./pages/Login";
import { Profile } from "./pages/Profile";
import { RoleGuard } from "./auth/RoleGuard";

const ProcurementSettings = lazy(() =>
  import("./modules/procurementConfig/pages/ProcurementSettings").then((m) => ({
    default: m.ProcurementSettings,
  }))
);
import { PublicTenders } from "./pages/PublicTenders";
import { PublicTenderDetail } from "./pages/PublicTenderDetail";
import { Evaluate } from "./pages/Evaluate";
import { VendorLanding } from "./pages/VendorLanding";

// Vendor Pages
import { VendorLogin } from "./modules/vendors/auth/VendorLogin";
import { VendorRegister } from "./modules/vendors/auth/VendorRegister";
import { VendorVerifyEmail } from "./modules/vendors/auth/VendorVerifyEmail";
import { VendorForgotPassword } from "./modules/vendors/auth/VendorForgotPassword";
import { VendorResetPassword } from "./modules/vendors/auth/VendorResetPassword";
import { VendorDashboard } from "./modules/vendors/pages/VendorDashboard";
import { VendorProfile } from "./modules/vendors/pages/VendorProfile";
import { VendorDocuments } from "./modules/vendors/pages/VendorDocuments";
import { VendorSubmissions } from "./modules/vendors/pages/VendorSubmissions";
import { VendorSubmissionDetail } from "./modules/vendors/pages/VendorSubmissionDetail";
import { TenderBrowse } from "./modules/vendors/pages/TenderBrowse";
import { VendorTenderDetail } from "./modules/vendors/pages/VendorTenderDetail";
import { VendorBidSubmissionPage } from "./modules/vendors/pages/VendorBidSubmissionPage";
import { VendorRFQList } from "./modules/vendors/pages/VendorRFQList";
import { VendorQuotationForm } from "./modules/vendors/pages/VendorQuotationForm";
import { VendorLayout } from "./modules/vendors/components/VendorLayout";
import { useVendorAuth } from "./modules/vendors/auth/VendorAuthContext";


// Protected Pages
import { Dashboard } from "./pages/Dashboard";
import Home from "./pages/Home";
import About from "./pages/About";

// Bid Committee Pages
import { BidDashboard } from "./modules/bidCommittee/pages/BidDashboard";
import { MeetingList } from "./modules/bidCommittee/pages/MeetingList";
import { MeetingForm } from "./modules/bidCommittee/pages/MeetingForm";
import { MeetingEdit } from "./modules/bidCommittee/pages/MeetingEdit";
import { MeetingDetail } from "./modules/bidCommittee/pages/MeetingDetail";
import { AwardSheetList } from "./modules/bidCommittee/pages/AwardSheetList";
import { AwardSheetDetail } from "./modules/bidCommittee/pages/AwardSheetDetail";
import { AwardSheetForm } from "./modules/bidCommittee/pages/AwardSheetForm";
import { AgendaReturnFormList } from "./modules/bidCommittee/pages/AgendaReturnFormList";
import { AgendaReturnFormDetail } from "./modules/bidCommittee/pages/AgendaReturnFormDetail";
import { AgendaReturnFormForm } from "./modules/bidCommittee/pages/AgendaReturnFormForm";
import { CEOEndorsementForm } from "./modules/bidCommittee/pages/CEOEndorsementForm";
import { CEOEndorsementDetail } from "./modules/bidCommittee/pages/CEOEndorsementDetail";

// Tender Pages
import { TenderDashboard } from "./modules/tenders/pages/TenderDashboard";
import { TenderList } from "./modules/tenders/pages/TenderList";
import { TenderForm } from "./modules/tenders/pages/TenderForm";
import { TenderDetail } from "./modules/tenders/pages/TenderDetail";
import { TenderEdit } from "./modules/tenders/pages/TenderEdit";
import { BidSubmissionDetail } from "./modules/tenders/pages/BidSubmissionDetail";
import { TechnicalEvaluationDetail } from "./modules/tenders/pages/TechnicalEvaluationDetail";
import { PendingApprovals } from "./modules/tenders/pages/PendingApprovals";
import { EvaluationReportDetail } from "./modules/tenders/pages/EvaluationReportDetail";
import { EvaluationReportApprovals } from "./modules/tenders/pages/EvaluationReportApprovals";

// Single Source Procurement Pages
import { SingleSourceList } from "./modules/singleSource/pages/SingleSourceList";
import { SingleSourceForm } from "./modules/singleSource/pages/SingleSourceForm";
import { SingleSourceDetail } from "./modules/singleSource/pages/SingleSourceDetail";
import { SingleSourceEdit } from "./modules/singleSource/pages/SingleSourceEdit";
import { ProjectSummarySheetList } from "./modules/projectSummarySheet/pages/ProjectSummarySheetList";
import { ProjectSummarySheetForm } from "./modules/projectSummarySheet/pages/ProjectSummarySheetForm";
import { ProjectSummarySheetDetail } from "./modules/projectSummarySheet/pages/ProjectSummarySheetDetail";
import { ProjectSummarySheetEdit } from "./modules/projectSummarySheet/pages/ProjectSummarySheetEdit";

// Contract Pages
import { ContractDashboard } from "./modules/contracts/pages/ContractDashboard";
import { ContractList } from "./modules/contracts/pages/ContractList";
import { ContractForm } from "./modules/contracts/pages/ContractForm";
import { ContractDetail } from "./modules/contracts/pages/ContractDetail";

// Requisition Pages
import { RequisitionList } from "./modules/requisitions/pages/RequisitionList";
import { RequisitionForm } from "./modules/requisitions/pages/RequisitionForm";
import { RequisitionDetail } from "./modules/requisitions/pages/RequisitionDetail";

// RFQ Pages
import { RFQList } from "./modules/rfq/pages/RFQList";
import { RFQForm } from "./modules/rfq/pages/RFQForm";
import { RFQDetail } from "./modules/rfq/pages/RFQDetail";
import { RFQComparison } from "./modules/rfq/pages/RFQComparison";
import { RFQAward } from "./modules/rfq/pages/RFQAward";

// Purchase Order Pages
import { POList } from "./modules/purchaseOrders/pages/POList";
import { PODetail } from "./modules/purchaseOrders/pages/PODetail";

// Admin Pages
import { VendorManagement } from "./modules/admin/pages/VendorManagement";
import { VendorDetail } from "./modules/admin/pages/VendorDetail";
import { VendorCategoryManagement } from "./modules/admin/pages/VendorCategoryManagement";
import { UserManagement } from "./modules/admin/pages/UserManagement";

// Layout Components
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <Text>Loading...</Text>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <Text>Loading...</Text>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== "ADMIN") {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const VendorProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { vendor, loading } = useVendorAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <Text>Loading...</Text>
        </div>
      </div>
    );
  }

  if (!vendor) {
    return <Navigate to="/vendor/login" replace />;
  }

  // Only block SUSPENDED vendors, allow REGISTERED, PENDING and ACTIVE
  if (vendor.status === "SUSPENDED") {
    return (
      <Container size="sm" py="xl">
        <Alert color="red" title="Account Suspended">
          Your vendor account has been suspended. Please contact support.
        </Alert>
      </Container>
    );
  }

  return <>{children}</>;
};

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { opened, toggle, close, navbar } = useMobileNavbar();

  const handleLogout = () => {
    logout();
    notifications.show({
      title: "Logged out",
      message: "You have been successfully logged out.",
      color: "blue",
    });
    navigate("/");
  };

  const navItems = [
    {
      label: "Dashboard",
      icon: IconDashboard,
      path: "/dashboard",
      iconColor: "#2563eb",
      roles: [
        "PROCUREMENT_OFFICER",
        "HEAD_OF_PROCUREMENT",
        "BID_COMMITTEE_MEMBER",
        "CEO",
        "ADMIN",
        "VIEWER",
      ],
    },
    {
      label: "Contracts",
      icon: IconFileText,
      path: "/contracts",
      iconColor: "#0891b2",
      roles: [
        "PROCUREMENT_OFFICER",
        "HEAD_OF_PROCUREMENT",
        "ADMIN",
        "VIEWER",
      ],
    },
    {
      label: "Bid Committee",
      icon: IconUsers,
      path: "/bid-committee",
      iconColor: "#7c3aed",
      roles: ["PROCUREMENT_OFFICER", "HEAD_OF_PROCUREMENT", "BID_COMMITTEE_MEMBER", "ADMIN"],
    },
    {
      label: "Tenders",
      icon: IconGavel,
      path: "/tenders",
      iconColor: "#16a34a",
      roles: ["PROCUREMENT_OFFICER", "HEAD_OF_PROCUREMENT", "ADMIN"],
    },
    {
      label: "Single Source Procurement",
      icon: IconFileText,
      path: "/single-source",
      iconColor: "#0d9488",
      roles: ["PROCUREMENT_OFFICER", "HEAD_OF_PROCUREMENT", "ADMIN"],
    },
    {
      label: "Project Summary Sheets",
      icon: IconClipboardList,
      path: "/project-summary-sheets",
      iconColor: "#0f766e",
      roles: ["PROCUREMENT_OFFICER", "HEAD_OF_PROCUREMENT", "ADMIN"],
    },
    {
      label: "RFQ",
      icon: IconFileText,
      path: "/rfq",
      iconColor: "#8b5cf6",
      roles: [
        "PROCUREMENT_OFFICER",
        "HEAD_OF_PROCUREMENT",
        "ADMIN",
      ],
    },
    {
      label: "Purchase Orders",
      icon: IconFileText,
      path: "/purchase-orders",
      iconColor: "#db2777",
      roles: [
        "PROCUREMENT_OFFICER",
        "HEAD_OF_PROCUREMENT",
        "ADMIN",
      ],
    },
    {
      label: "My Approvals",
      icon: IconClock,
      path: "/tenders/pending-approvals",
      iconColor: "#ea580c",
      roles: ["ADMIN", "HEAD_OF_PROCUREMENT", "PROCUREMENT_OFFICER", "BID_COMMITTEE_MEMBER", "CEO"],
    },
    {
      label: "Calendar",
      icon: IconCalendar,
      path: "/calendar",
      iconColor: "#0284c7",
      roles: [
        "PROCUREMENT_OFFICER",
        "HEAD_OF_PROCUREMENT",
        "BID_COMMITTEE_MEMBER",
        "CEO",
        "ADMIN",
        "VIEWER",
      ],
    },
    {
      label: "Vendor Management",
      icon: IconBuilding,
      path: "/admin/vendors",
      iconColor: "#f59e0b",
      roles: ["PROCUREMENT_OFFICER", "HEAD_OF_PROCUREMENT", "ADMIN"],
    },
    {
      label: "User Management",
      icon: IconUserCog,
      path: "/admin/users",
      iconColor: "#dc2626",
      roles: ["ADMIN"],
    },
  ];

  const filteredNavItems = user?.role === "ADMIN"
    ? navItems.filter((item) => item.path === "/admin/users")
    : navItems.filter((item) => item.roles.includes(user?.role || ""));

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={navbar}
      padding="md"
    >
      <AppShell.Header>
        <Group justify="space-between" h="100%" px="md">
          <Group>
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <IconShield size={32} className="text-blue-600" />
            <Text size="lg" fw={700}>
              HMH Procurement
            </Text>
          </Group>
          <Group>
            <UnstyledButton onClick={() => navigate("/profile")}>
              <Text size="sm" c="blue" fw={600}>
                {(user?.role || "").replace(/_/g, " ")}
              </Text>
            </UnstyledButton>
            <Button
              variant="subtle"
              size="sm"
              leftSection={<IconLogout size={16} />}
              onClick={handleLogout}
            >
              Logout
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        <Stack gap="md">
          {filteredNavItems.map((item) => (
            <UnstyledButton
              key={item.path}
              className="p-3 rounded-md hover:bg-gray-100 transition-colors"
              onClick={() => {
                navigate(item.path);
                close();
              }}
            >
              <Group>
                <item.icon size={20} style={{ color: item.iconColor }} />
                <Text>{item.label}</Text>
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>{children}</AppShell.Main>
    </AppShell>
  );
};

const App: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/public/tenders" element={<PublicTenders />} />
      <Route path="/public/tenders/:id" element={<PublicTenderDetail />} />
      <Route path="/evaluate/:token" element={<Evaluate />} />

      {/* Vendor Landing & Auth Routes */}
      <Route path="/vendor/login" element={<VendorLogin />} />
      <Route path="/vendor/register" element={<VendorRegister />} />
      <Route path="/vendor/verify-email" element={<VendorVerifyEmail />} />
      <Route path="/vendor/forgot-password" element={<VendorForgotPassword />} />
      <Route path="/vendor/reset-password" element={<VendorResetPassword />} />

      {/* Vendor Portal Routes (Protected) */}
      <Route
        path="/vendor/dashboard"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorDashboard />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />
      <Route
        path="/vendor/profile"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorProfile />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />
      <Route
        path="/vendor/documents"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorDocuments />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />
      <Route
        path="/vendor/submissions"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorSubmissions />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />
      <Route
        path="/vendor/submissions/:tenderId/:submissionId"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorSubmissionDetail />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />
      <Route
        path="/vendor/tenders"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <TenderBrowse />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />
      <Route
        path="/vendor/tenders/:id"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorTenderDetail />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />
      <Route
        path="/vendor/tenders/:tenderId/submit-bid"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorBidSubmissionPage />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />
      <Route
        path="/vendor/rfq"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorRFQList />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />
      <Route
        path="/vendor/rfq/:rfqId"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorRFQList />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />
      <Route
        path="/vendor/rfq/:rfqId/quotation"
        element={
          <VendorProtectedRoute>
            <VendorLayout>
              <VendorQuotationForm />
            </VendorLayout>
          </VendorProtectedRoute>
        }
      />

      {/* Vendor Landing Page (Public) */}
      <Route path="/vendor" element={<VendorLanding />} />


      {/* Protected Routes */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Profile />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile/procurement-settings"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RoleGuard roles={["PROCUREMENT_OFFICER"]}>
                <Suspense
                  fallback={
                    <Group justify="center" py="xl">
                      <Loader />
                    </Group>
                  }
                >
                  <ProcurementSettings />
                </Suspense>
              </RoleGuard>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Dashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Bid Committee Routes */}
      <Route
        path="/bid-committee/*"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Routes>
                <Route index element={<BidDashboard />} />
                <Route path="dashboard" element={<BidDashboard />} />
                <Route path="meetings">
                  <Route index element={<MeetingList />} />
                  <Route path="new" element={<MeetingForm />} />
                  <Route path=":id/edit" element={<MeetingEdit />} />
                  <Route path=":id" element={<MeetingDetail />} />
                </Route>
                <Route path="award-sheets">
                  <Route index element={<AwardSheetList />} />
                  <Route path="new" element={<AwardSheetForm />} />
                  <Route path=":id/edit" element={<AwardSheetForm />} />
                  <Route path=":id" element={<AwardSheetDetail />} />
                </Route>
                <Route path="agenda-return-forms">
                  <Route index element={<AgendaReturnFormList />} />
                  <Route path="new" element={<AgendaReturnFormForm />} />
                  <Route path=":id/edit" element={<AgendaReturnFormForm />} />
                  <Route path=":id" element={<AgendaReturnFormDetail />} />
                </Route>
                <Route path="endorsements">
                  <Route path="new" element={<CEOEndorsementForm />} />
                  <Route path=":id/edit" element={<CEOEndorsementForm />} />
                  <Route path=":id" element={<CEOEndorsementDetail />} />
                </Route>
              </Routes>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Tender Routes */}
      <Route
        path="/tenders/*"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Routes>
                <Route index element={<TenderList />} />
                <Route path="dashboard" element={<TenderDashboard />} />
                <Route path="new" element={<TenderForm />} />
                <Route path=":id/edit" element={<TenderEdit />} />
                <Route path=":id" element={<TenderDetail />} />
                <Route path=":tenderId/bid-submissions/:submissionId" element={<BidSubmissionDetail />} />
                <Route path=":tenderId/technical-evaluations/:evaluationId" element={<TechnicalEvaluationDetail />} />
                <Route
                  path="pending-approvals"
                  element={<PendingApprovals />}
                />
                <Route
                  path=":id/evaluation-report"
                  element={<EvaluationReportDetail />}
                />
                <Route
                  path="evaluation-reports/approvals"
                  element={<EvaluationReportApprovals />}
                />
              </Routes>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Single Source Procurement Routes */}
      <Route
        path="/single-source/*"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Routes>
                <Route index element={<SingleSourceList />} />
                <Route path="new" element={<SingleSourceForm />} />
                <Route path=":id" element={<SingleSourceDetail />} />
                <Route path=":id/edit" element={<SingleSourceEdit />} />
              </Routes>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/project-summary-sheets/*"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Routes>
                <Route index element={<ProjectSummarySheetList />} />
                <Route path="new" element={<ProjectSummarySheetForm />} />
                <Route path=":id/edit" element={<ProjectSummarySheetEdit />} />
                <Route path=":id" element={<ProjectSummarySheetDetail />} />
              </Routes>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Contract Routes */}
      <Route
        path="/contracts/*"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Routes>
                <Route index element={<ContractList />} />
                <Route path="dashboard" element={<ContractDashboard />} />
                <Route path="new" element={<ContractForm />} />
                <Route path=":id" element={<ContractDetail />} />
                <Route path=":id/edit" element={<ContractForm />} />
              </Routes>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Requisition Routes */}
      <Route
        path="/requisitions/*"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Routes>
                <Route index element={<RequisitionList />} />
                <Route path="new" element={<RequisitionForm />} />
                <Route path=":id" element={<RequisitionDetail />} />
                <Route path=":id/edit" element={<RequisitionForm />} />
              </Routes>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* RFQ Routes */}
      <Route
        path="/rfq"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RFQList />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rfq/*"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Routes>
                <Route path="new" element={<RFQForm />} />
                <Route path=":id" element={<RFQDetail />} />
                <Route path=":id/edit" element={<RFQForm />} />
                <Route path=":id/comparison" element={<RFQComparison />} />
                <Route path=":id/award" element={<RFQAward />} />
              </Routes>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Purchase Order Routes */}
      <Route
        path="/purchase-orders/*"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Routes>
                <Route index element={<POList />} />
                <Route path=":id" element={<PODetail />} />
              </Routes>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Admin Vendor Management Routes */}
      <Route
        path="/admin/vendors/*"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Routes>
                <Route index element={<VendorManagement />} />
                <Route path="categories" element={<VendorCategoryManagement />} />
                <Route path=":vendorId" element={<VendorDetail />} />
              </Routes>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Admin User Management Routes */}
      <Route
        path="/admin/users"
        element={
          <AdminRoute>
            <AppLayout>
              <UserManagement />
            </AppLayout>
          </AdminRoute>
        }
      />

      {/* Legacy Routes */}
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Home />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/about"
        element={
          <ProtectedRoute>
            <AppLayout>
              <About />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default App;
